var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MDS_COLORS_BACKGROUND_ERROR, MDS_COLORS_BACKGROUND_ERROR_WEAK, MDS_COLORS_BACKGROUND_RECOMMENDATION_WEAK, MDS_COLORS_INK_ACCENT, MDS_COLORS_INK_RECOMMENDATION, } from '@rappi/mustache-b2b-foundations';
import { Button, Chip, FilledArrowUpRightIcon, FlexContainer, Loader, SearchOutlineIcon, SparklesIcon, TextField, TrashBinOutlineIcon, Typography, } from '@rappi/rests-commons-front';
import { useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { CopyToClipboard } from '../../commons/copy-to-clipboard/copy-to-clipboard';
import { Drawer } from '../../commons/drawer/drawer';
import { LoadingDots } from '../../commons/loading-dots/loading-dots';
import { useDebounce } from '../../hooks/use-debounce';
import { useMenuGenerated } from '../../hooks/use-menu-generated';
import { menuGeneratorService } from '../../services/menu-generator.service';
import { formatDate } from '../../utils/format-date';
import { MenuGeneratorSidebar } from '../menu-generator-sidebar/menu-generator-sidebar';
import { ButtonStyled, customTableStyles, TableContainer } from './styles';
export const MenuGeneratorTable = () => {
    const { t } = useTranslation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const searchValueDebounced = useDebounce(searchValue, 500);
    const { data, error, isLoading, mutate } = useMenuGenerated(page, size, searchValueDebounced);
    const columns = [
        {
            wrap: true,
            name: t('menuGenerator.table.generated.columns.name'),
            selector: (row) => row.restaurant_name,
        },
        {
            name: t('menuGenerator.table.generated.columns.createdAt'),
            cell: (row) => {
                if (row.status === 'PENDING' ||
                    row.status === 'PROCESSING' ||
                    row.status === 'RUNNING' ||
                    row.created_at === null) {
                    return (_jsx(Chip, { leftIcon: _jsx(SparklesIcon, { size: 14, color: MDS_COLORS_INK_RECOMMENDATION }), label: _jsxs("div", { style: { display: 'flex', gap: '2px' }, children: [t('menuGenerator.table.generated.ia.pending'), " ", _jsx(LoadingDots, {})] }), customBgColor: MDS_COLORS_BACKGROUND_RECOMMENDATION_WEAK, customTextColor: MDS_COLORS_INK_RECOMMENDATION }));
                }
                if (row.status === 'FAILED' || row.status === 'CORRUPTED') {
                    return (_jsx(Chip, { leftIcon: _jsx(SparklesIcon, { size: 14, color: MDS_COLORS_BACKGROUND_ERROR }), label: t('menuGenerator.table.generated.ia.error'), customBgColor: MDS_COLORS_BACKGROUND_ERROR_WEAK, customTextColor: MDS_COLORS_BACKGROUND_ERROR }));
                }
                return formatDate(row.created_at);
            },
        },
        {
            wrap: true,
            name: t('menuGenerator.table.generated.columns.actions'),
            cell: (row) => {
                var _a;
                if (row.status === 'COMPLETED') {
                    return (_jsxs(FlexContainer, { gap: 32, children: [_jsx(ButtonStyled, { fullWidth: true, variant: "link", text: t('menuGenerator.table.generated.actions.view'), iconLeft: _jsx(FilledArrowUpRightIcon, { size: 16, color: MDS_COLORS_INK_ACCENT }), onClick: () => {
                                    var _a;
                                    window.open((_a = row.spreadsheet_url) !== null && _a !== void 0 ? _a : '', '_blank');
                                } }), _jsx(CopyToClipboard, { fullWidth: true, variant: "link", textToCopy: (_a = row.spreadsheet_url) !== null && _a !== void 0 ? _a : '', text: t('menuGenerator.table.generated.actions.copyLink') })] }));
                }
                if (row.status === 'FAILED' || row.status === 'CORRUPTED') {
                    return (_jsx(ButtonStyled, { fullWidth: true, variant: "link", text: t('menuGenerator.table.generated.actions.delete'), iconLeft: _jsx(TrashBinOutlineIcon, { size: 16, color: MDS_COLORS_INK_ACCENT }), 
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                            yield menuGeneratorService.deleteMenuGenerated(row.id);
                            yield mutate({
                                content: data.content.filter((item) => item.id !== row.id),
                                total_elements: data.total_elements - 1,
                            });
                        }) }));
                }
                return null;
            },
            style: {
                minWidth: 'initial',
            },
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsxs(FlexContainer, { width: "100%", justifyContent: "space-between", gap: 16, alignItems: "center", children: [_jsx(Typography, { component: "subheadline", semibold: true, children: t(`menuGenerator.table.generated.header.${data.total_elements > 0 ? 'generated' : 'initial'}`, {
                            number: data.total_elements,
                        }) }), _jsxs(FlexContainer, { gap: 16, children: [!error && (_jsx(TextField, { fullWidth: true, value: searchValue, onChange: (e) => {
                                    setSearchValue(e.target.value);
                                }, label: t('menuGenerator.table.generated.actions.search'), icon: _jsx(SearchOutlineIcon, {}), size: "s", variant: "searchBar", onClickDeleteInput: () => {
                                    setSearchValue('');
                                } })), _jsx(Button, { fullWidth: true, text: t('menuGenerator.table.generated.actions.generate'), onClick: () => {
                                    setSidebarOpen(true);
                                } })] })] }), !error && (_jsx(TableContainer, { children: _jsx(DataTable, { pagination: true, responsive: true, persistTableHead: true, paginationServer: true, columns: columns, data: data.content, customStyles: customTableStyles, progressPending: isLoading, noDataComponent: null, paginationPerPage: size, paginationTotalRows: data.total_elements, paginationDefaultPage: 1, paginationRowsPerPageOptions: [10, 20, 50], progressComponent: _jsx(Loader, { fullscreen: false }), paginationComponentOptions: {
                        rangeSeparatorText: t('menuGenerator.table.pagination.rangeSeparatorText'),
                        rowsPerPageText: t('menuGenerator.table.pagination.rowsPerPageText'),
                        selectAllRowsItemText: t('menuGenerator.table.pagination.selectAllRowsItemText'),
                    }, onChangePage: (page) => {
                        setPage(page);
                    }, onChangeRowsPerPage: (currentRowsPerPage) => {
                        setSize(currentRowsPerPage);
                    } }) })), _jsx(Drawer, { isOpen: sidebarOpen, zIndex: 1300, width: "487px", side: "right", onClose: () => {
                    setSidebarOpen(false);
                }, children: _jsx(MenuGeneratorSidebar, { mutateData: mutate, onClose: () => {
                        setSidebarOpen(false);
                    } }) })] }));
};
